import { Badge as BadgeComponent, Image } from 'antd';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { DEVICE } from 'constants/app/breakpoints';
import type { TPlacementCorner, TShapeType, TSizeType } from 'interfaces';

export const SIZE_STYLES: Record<
  TSizeType,
  Record<
    'wrapper' | 'avatar' | 'badge' | 'text' | 'secondaryText',
    FlattenSimpleInterpolation
  >
> = {
  huge: {
    wrapper: css`
      grid-template-columns: fit-content(80px) 1fr;
      gap: 11px 20px;
    `,
    avatar: css`
      font-size: 24px;
      width: 80px;
      height: 80px;
    `,
    badge: css`
      font-size: 32px;
      width: 32px;
      height: 32px;
    `,
    text: css`
      font-size: 20px;
      line-height: 24px;
      align-self: flex-end;
    `,
    secondaryText: css`
      font-size: 16px;
      line-height: 24px;
      align-self: flex-start;
    `
  },
  large: {
    wrapper: css`
      grid-template-columns: fit-content(48px) 1fr;
      gap: 4px 12px;
    `,
    avatar: css`
      font-size: 14px;
      width: 48px;
      height: 48px;
    `,
    badge: css`
      font-size: 20px;
      width: 20px;
      height: 20px;
    `,
    text: css`
      font-size: 16px;
    `,
    secondaryText: css`
      font-size: 14px;
    `
  },
  medium: {
    wrapper: css`
      grid-template-columns: fit-content(36px) 1fr;
      gap: 0 8px;
    `,
    avatar: css`
      font-size: 12px;
      width: 36px;
      height: 36px;
    `,
    badge: css`
      font-size: 16px;
      width: 16px;
      height: 16px;
    `,
    text: css`
      font-size: 14px;
    `,
    secondaryText: css`
      font-size: 12px;
    `
  },
  small: {
    wrapper: css`
      grid-template-columns: fit-content(24px) 1fr;
      gap: 0 8px;
    `,
    avatar: css`
      font-size: 10px;
      width: 24px;
      height: 24px;
    `,
    badge: css`
      font-size: 12px;
      width: 12px;
      height: 12px;
    `,
    text: css`
      font-size: 12px;
    `,
    secondaryText: css`
      font-size: 10px;
    `
  }
};

export const SHAPE_STYLES: Record<
  TShapeType,
  Record<'avatar', Record<TSizeType, FlattenSimpleInterpolation>>
> = {
  round: {
    avatar: {
      huge: css`
        border-radius: 20px;
      `,
      large: css`
        border-radius: 12px;
      `,
      medium: css`
        border-radius: 8px;
      `,
      small: css`
        border-radius: 8px;
      `
    }
  },
  circle: {
    avatar: {
      huge: css`
        border-radius: 50%;
      `,
      large: css`
        border-radius: 50%;
      `,
      medium: css`
        border-radius: 50%;
      `,
      small: css`
        border-radius: 50%;
      `
    }
  }
};

export const ONE_LINE_STYLES: Record<
  'wrapper' | 'avatar',
  FlattenSimpleInterpolation
> = {
  wrapper: css`
    grid-template-rows: 1fr;
    row-gap: 0;
  `,
  avatar: css`
    grid-row-start: 1;
    grid-row-end: 2;
  `
};

export const Avatar = styled(Image)`
  grid-row-start: 1;
  grid-row-end: 3;
`;

export const AvatarFallback = styled.div<{
  $bgColor?: string;
}>`
  grid-row-start: 1;
  grid-row-end: 3;
  background: ${props => props.$bgColor || 'var(--v3-black-1)'};
  color: var(--v3-black);
  display: flex;
  align-items: center;
  justify-content: center;

  &&& * {
    width: 100%;
    height: 100%;
  }
`;

export const PrimaryText = styled.div<{
  $isTextHighlighted?: boolean;
}>`
  font-weight: 500;
  line-height: 20px;
  color: var(--v3-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &&& {
    background: initial;
    -webkit-text-fill-color: initial;
  }
  ${props =>
    props.$isTextHighlighted &&
    css`
      &:hover,
      &:focus {
        color: var(--v3-primary-color);
      }
    `}
`;

export const SecondaryText = styled.div<{
  $isTextBlur?: boolean;
}>`
  font-weight: 500;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${props =>
    props.$isTextBlur
      ? 'var(--v3-black-45)'
      : 'var(--v3-text-color-secondary)'};
  &&& {
    background: initial;
    -webkit-text-fill-color: initial;
  }
`;

export const Badge = styled(BadgeComponent)<{
  $placement?: TPlacementCorner;
  $size?: TSizeType;
}>`
  &&& {
    grid-row-start: 1;
    grid-row-end: 3;

    & .ant-scroll-number-custom-component {
      transform: none;
      transform-origin: unset;
      ${props =>
        props.$placement === 'topLeft' &&
        css`
          left: 0;
          top: 0;
          right: unset;
          bottom: unset;
        `}
      ${props =>
        props.$placement === 'topRight' &&
        css`
          right: 0;
          top: 0;
          left: unset;
          bottom: unset;
        `}
      ${props =>
        props.$placement === 'bottomLeft' &&
        css`
          left: 0;
          bottom: 0;
          right: unset;
          top: unset;
        `}
      ${props =>
        props.$placement === 'bottomRight' &&
        css`
          right: 0;
          bottom: 0;
          left: unset;
          top: unset;
        `}
      ${props => SIZE_STYLES[props.$size || 'medium'].badge}
    }
  }
`;

export const AvatarTextWrapper = styled.div<{
  $size: TSizeType;
  $shape: TShapeType;
  $oneLine?: boolean;
  $clickable?: boolean;
  $fixedWhite?: boolean;
  $plainAvatar?: boolean;
}>`
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: fit-content(44px) 1fr;
  align-items: center;
  ${props => SIZE_STYLES[props.$size].wrapper}
  ${props => props.$oneLine && ONE_LINE_STYLES.wrapper}
  ${props =>
    props.$clickable &&
    css`
      cursor: pointer;
    `}
  ${Avatar} {
    ${props => SIZE_STYLES[props.$size].avatar}
    ${props => SHAPE_STYLES[props.$shape].avatar[props.$size]}
    ${props => props.$oneLine && ONE_LINE_STYLES.avatar}
  }

  ${AvatarFallback} {
    ${props => SIZE_STYLES[props.$size].avatar}
    ${props => SHAPE_STYLES[props.$shape].avatar[props.$size]}
    ${props => props.$oneLine && ONE_LINE_STYLES.avatar}
    ${props =>
      props.$plainAvatar &&
      css`
        color: var(--v3-black-0);
      `}
  }

  ${PrimaryText} {
    ${props => SIZE_STYLES[props.$size].text}
    ${props =>
      props.$fixedWhite &&
      css`
        color: var(--v3-white);
      `}
  }

  ${SecondaryText} {
    ${props => SIZE_STYLES[props.$size].secondaryText}
    ${props =>
      props.$fixedWhite &&
      css`
        color: var(--v3-white);
      `}
      ${props =>
      props.$plainAvatar &&
      css`
        color: var(--v3-black-0);
      `}
  }
  @media screen and ${DEVICE.xl} {
    ${props =>
      props.$plainAvatar &&
      css`
        grid-template-columns: none;
      `}
  }
`;

export const NoAvatarFallback = styled.div`
  grid-row-start: 1;
  grid-row-end: 3;
`;
